import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography,
  Container, Alert, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login, register } from '../services/api';

export default function Login() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      if (isLogin) {
        const res = await login({
          email: form.email,
          password: form.password
        });
        localStorage.setItem('token', res.data.access_token);
        navigate('/dashboard');
      } else {
        await register({
          username: form.username,
          email: form.email,
          password: form.password,
          is_anonymous: false
        });
        const res = await login({
          email: form.email,
          password: form.password
        });
        localStorage.setItem('token', res.data.access_token);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Container maxWidth="xs">
        <Box sx={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          p: 4,
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          {/* Logo */}
          <Typography variant="h3" sx={{
            textAlign: 'center',
            color: '#4ade80',
            fontWeight: 900,
            mb: 0.5
          }}>
            🌿 Imara
          </Typography>
          <Typography sx={{
            textAlign: 'center',
            color: 'rgba(255,255,255,0.5)',
            mb: 4,
            fontSize: '0.85rem'
          }}>
            Grow stronger, one day at a time
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!isLogin && (
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={form.username}
              onChange={handleChange}
              sx={inputStyle}
              margin="normal"
            />
          )}

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            sx={inputStyle}
            margin="normal"
          />

          {/* Password field with eye icon */}
          <Box sx={{ position: 'relative', mt: 2, mb: 1 }}>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              sx={inputStyle}
            />
            <Box
              onClick={() => setShowPassword(s => !s)}
              sx={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#7fa8c8',
                display: 'flex',
                alignItems: 'center',
                zIndex: 10,
                '&:hover': { color: '#4ade80' },
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </Box>
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.5,
              background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
              color: '#000',
              fontWeight: 700,
              borderRadius: 2,
              '&:hover': {
                background: 'linear-gradient(90deg, #22d3ee, #4ade80)',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : (isLogin ? 'Login' : 'Create Account')}
          </Button>

          <Typography
            onClick={() => navigate('/forgot-password')}
            sx={{
              textAlign: 'center',
              mt: 2,
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              fontSize: '0.82rem',
              '&:hover': { color: '#4ade80' }
            }}
          >
            Forgot password?
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 2,
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              fontSize: '0.85rem',
              '&:hover': { color: '#4ade80' }
            }}
            onClick={() => setIsLogin(!isLogin)}
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Login"}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: '#4ade80' },
    '&.Mui-focused fieldset': { borderColor: '#4ade80' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4ade80' },
};